import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Fila del listado — matchea el RETURNS TABLE de get_user_tracking_list
export interface UserTrackingRow {
  user_id: string;
  full_name: string | null;
  email: string | null;
  organization_id: string | null;
  organization_name: string | null;
  primary_cohort_id: string | null;
  primary_cohort_name: string | null;
  cohorts_count: number;
  content_percent: number;
  deliverables_submitted: number;
  deliverables_total: number;
  projects_created: number;
  projects_submitted: number;
  challenge_solutions_count: number;
  diagnostic_completed: boolean;
  last_activity_at: string | null;
  is_at_risk: boolean;
  license_code: string;
  total_count: number;
}

export type LicenseCode = 'free' | 'starter' | 'pro' | 'enterprise' | null | undefined;

export type TriState = boolean | null | undefined;

export interface UserTrackingFilters {
  orgId?: string | null;
  cohortId?: string | null;
  onlyActiveCohorts?: boolean;
  onlyWithActivity?: boolean;
  search?: string;
  sortBy?: 'last_activity' | 'name' | 'content_percent' | 'deliverables';
  sortDir?: 'asc' | 'desc';
  licenseType?: LicenseCode;
  activityFrom?: string | null;   // YYYY-MM-DD
  activityTo?: string | null;     // YYYY-MM-DD
  hasProject?: TriState;
  hasDeliverables?: TriState;
  hasCohort?: TriState;
  page?: number;
  pageSize?: number;
}

// Detalle drill-down — jsonb devuelto por get_user_tracking_detail
export interface UserTrackingDetail {
  profile: {
    id: string;
    full_name: string | null;
    email: string | null;
    avatar_url: string | null;
    company: string | null;
    position: string | null;
    area: string | null;
    organization_id: string | null;
    organization_name: string | null;
  } | null;
  cohorts: Array<{
    cohort_id: string;
    cohort_name: string;
    start_date: string | null;
    end_date: string | null;
    program_id: string | null;
    program_name: string | null;
  }>;
  deliverables: Array<{
    id: string;
    name: string;
    status: string;
    file_url: string | null;
    workshop_id: string | null;
    submitted_at: string | null;
    cohort_id: string | null;
    solution_type: number | null;
  }>;
  projects: Array<{
    id: string;
    name: string;
    status: string | null;
    submitted: boolean;
    submitted_at: string | null;
    is_approved: boolean;
    cohort_id: string | null;
    solution_type: number | null;
    created_at: string | null;
  }>;
  challenge_solutions: Array<{
    id: string;
    challenge_id: string;
    created_at: string;
  }>;
  diagnostics: Array<{
    id: string;
    maturity_level: string | number | null;
    completed_at: string | null;
  }>;
  content_progress: Array<{
    content_id: string;
    completed: boolean;
    completed_at: string | null;
  }>;
  license: {
    id?: string;
    status?: string;
    provider?: string | null;
    current_period_start?: string | null;
    current_period_end?: string | null;
    license_code: string;
    license_name?: string;
    billing_cycle?: string | null;
    price_amount?: number | null;
    currency?: string | null;
  };
}

/**
 * Hook para el listado paginado del módulo /user-tracking.
 * Consume RPC `get_user_tracking_list`. RLS ya limita el scope según el rol
 * del caller (super_admin ve todo, admin ve su org).
 */
export function useUserTrackingList(filters: UserTrackingFilters) {
  const {
    orgId = null,
    cohortId = null,
    onlyActiveCohorts = true,
    onlyWithActivity = true,
    search = '',
    sortBy = 'last_activity',
    sortDir = 'desc',
    licenseType = null,
    activityFrom = null,
    activityTo = null,
    hasProject = null,
    hasDeliverables = null,
    hasCohort = null,
    page = 1,
    pageSize = 50,
  } = filters;

  return useQuery({
    queryKey: ['user-tracking-list', {
      orgId, cohortId, onlyActiveCohorts, onlyWithActivity, search, sortBy, sortDir, licenseType,
      activityFrom, activityTo, hasProject, hasDeliverables, hasCohort, page, pageSize,
    }],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_user_tracking_list', {
        _org_id: orgId,
        _cohort_id: cohortId,
        _only_active_cohorts: onlyActiveCohorts,
        _only_with_activity: onlyWithActivity,
        _search: search || null,
        _sort_by: sortBy,
        _sort_dir: sortDir,
        _license_type: licenseType || null,
        _activity_from: activityFrom || null,
        _activity_to: activityTo || null,
        _has_project: hasProject ?? null,
        _has_deliverables: hasDeliverables ?? null,
        _has_cohort: hasCohort ?? null,
        _page: page,
        _page_size: pageSize,
      });
      if (error) throw error;
      const rows = (data ?? []) as UserTrackingRow[];
      return {
        rows,
        totalCount: rows[0]?.total_count ?? 0,
      };
    },
    staleTime: 30_000,
  });
}

export interface UserTrackingStats {
  total: number;
  avg_content_percent: number;
  with_project: number;
  diagnostic_completed: number;
  at_risk: number;
}

/**
 * Hook para los KPIs globales del filtro (agregados sobre TODOS los users
 * del filtro, no solo la página visible).
 */
export function useUserTrackingStats(filters: Pick<UserTrackingFilters, 'orgId' | 'cohortId' | 'onlyActiveCohorts' | 'onlyWithActivity' | 'search' | 'licenseType' | 'activityFrom' | 'activityTo' | 'hasProject' | 'hasDeliverables' | 'hasCohort'>) {
  const {
    orgId = null,
    cohortId = null,
    onlyActiveCohorts = true,
    onlyWithActivity = true,
    search = '',
    licenseType = null,
    activityFrom = null,
    activityTo = null,
    hasProject = null,
    hasDeliverables = null,
    hasCohort = null,
  } = filters;

  return useQuery({
    queryKey: ['user-tracking-stats', {
      orgId, cohortId, onlyActiveCohorts, onlyWithActivity, search, licenseType,
      activityFrom, activityTo, hasProject, hasDeliverables, hasCohort,
    }],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_user_tracking_stats', {
        _org_id: orgId,
        _cohort_id: cohortId,
        _only_active_cohorts: onlyActiveCohorts,
        _only_with_activity: onlyWithActivity,
        _search: search || null,
        _license_type: licenseType || null,
        _activity_from: activityFrom || null,
        _activity_to: activityTo || null,
        _has_project: hasProject ?? null,
        _has_deliverables: hasDeliverables ?? null,
        _has_cohort: hasCohort ?? null,
      });
      if (error) throw error;
      return (data ?? { total: 0, avg_content_percent: 0, with_project: 0, diagnostic_completed: 0, at_risk: 0 }) as UserTrackingStats;
    },
    staleTime: 30_000,
  });
}

/**
 * Hook para el detalle drill-down de un usuario específico.
 * Enabled solo cuando userId no es null (típicamente al abrir el modal).
 */
export function useUserTrackingDetail(userId: string | null) {
  return useQuery({
    queryKey: ['user-tracking-detail', userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await (supabase as any).rpc('get_user_tracking_detail', {
        _user_id: userId,
      });
      if (error) throw error;
      return data as UserTrackingDetail | null;
    },
    enabled: !!userId,
    staleTime: 60_000,
  });
}
